<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { useBase } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';

// T-186 Fase 4 — a planilha voltando.
//
// A tela existe por causa de uma regra só: ninguém importa sem ver antes. A prévia não é um
// resumo do arquivo, é a importação de verdade rodada e desfeita no servidor, então o que está
// escrito aqui é o que vai acontecer. Só depois de ler é que aparece o botão de aplicar, e mesmo
// ele não vai ao ar: escreve no rascunho, que ainda passa pela tela de publicar.
const { t } = useI18n();
const { irPara } = useBase();

const arquivo = ref(null);
const conteudo = ref('');
const previa = ref(null);
const lendo = ref(false);
const aplicando = ref(false);
const baixando = ref(false);
// Em que passo a importação está, para a espera ter texto em vez de um botão girando.
const fase = ref('');

const total = computed(() => {
  const p = previa.value;
  if (!p) return 0;
  return (p.criados || 0) + (p.atualizados || 0) + (p.ausentes?.length || 0);
});

// A base de hoje não tem causa raiz em nenhuma resposta, então a prévia de uma planilha inteira
// vem com 336 avisos idênticos. Repetidos um por linha, eles escondem o aviso que importa.
// Agrupados pela mensagem, viram uma linha com a contagem e as primeiras linhas como amostra.
const avisosAgrupados = computed(() => {
  const mapa = new Map();
  (previa.value?.avisos || []).forEach(aviso => {
    if (!mapa.has(aviso.mensagem)) mapa.set(aviso.mensagem, []);
    mapa.get(aviso.mensagem).push(aviso.linha);
  });
  return [...mapa.entries()]
    .map(([mensagem, linhas]) => ({ mensagem, linhas }))
    .sort((a, b) => b.linhas.length - a.linhas.length);
});

const exportar = async () => {
  baixando.value = true;
  try {
    const { data } = await SandboxAPI.exportar();
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `base-do-bira-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('BASE_IMPORTAR.ERRO_EXPORTAR'));
  } finally {
    baixando.value = false;
  }
};

const escolher = async evento => {
  const escolhido = evento.target.files?.[0];
  if (!escolhido) return;
  arquivo.value = escolhido;
  previa.value = null;
  lendo.value = true;
  try {
    conteudo.value = await escolhido.text();
    const { data } = await SandboxAPI.importar(conteudo.value, false);
    previa.value = data;
  } catch (error) {
    useAlert(t('BASE_IMPORTAR.ERRO_PREVIA'));
  } finally {
    lendo.value = false;
  }
};

// A importação roda em segundo plano (clonar a base, criar as novas e desativar as que saem
// passa de mil escritas, e isso estourava o limite de tempo do request). Aqui a tela pergunta
// de tempos em tempos onde ela está, para quem espera saber que algo acontece.
const INTERVALO_MS = 2000;
let relogio = null;

const pararDeAcompanhar = () => {
  if (relogio) clearInterval(relogio);
  relogio = null;
};

const concluir = data => {
  pararDeAcompanhar();
  aplicando.value = false;
  useAlert(
    t('BASE_IMPORTAR.APLICADA', {
      n: (data.criados || 0) + (data.atualizados || 0),
    })
  );
  irPara('capitao_base_editar');
};

const falhar = () => {
  pararDeAcompanhar();
  aplicando.value = false;
  useAlert(t('BASE_IMPORTAR.ERRO_APLICAR'));
};

const acompanhar = async () => {
  try {
    const { data } = await SandboxAPI.importacao();
    if (data.estado === 'concluida') return concluir(data);
    if (data.estado === 'erro') return falhar();
    // `processando` ou nulo: segue esperando. Nulo acontece na janela entre o clique e o
    // servidor registrar, e tratar como falha faria a tela desistir cedo demais.
    fase.value = data.fase || fase.value;
    return undefined;
  } catch (error) {
    return falhar();
  }
};

const aplicar = async () => {
  aplicando.value = true;
  fase.value = 'na_fila';
  try {
    await SandboxAPI.importar(conteudo.value, true);
    relogio = setInterval(acompanhar, INTERVALO_MS);
  } catch (error) {
    falhar();
  }
};

onBeforeUnmount(pararDeAcompanhar);
</script>

<template>
  <SettingsLayout>
    <template #body>
      <div class="flex flex-col gap-3.5 w-full min-w-0 max-w-[880px]">
        <div class="flex items-baseline gap-2.5">
          <button
            type="button"
            class="text-[15px] text-n-slate-11 hover:text-n-slate-12"
            @click="irPara('capitao_base')"
          >
            {{ $t('BASE_IMPORTAR.VOLTAR') }}
          </button>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{ $t('BASE_IMPORTAR.HEADER') }}
          </h1>
        </div>

        <!-- Baixar antes de subir: a planilha da nossa exportação traz o id do sistema, e é ele
             que faz a volta reconhecer cada resposta em vez de criar uma cópia ao lado. -->
        <section
          class="rounded-md border border-solid border-n-weak bg-n-solid-1 px-[18px] py-4 flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <div class="flex-1 min-w-[280px]">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t('BASE_IMPORTAR.BAIXAR.TITULO') }}
            </h2>
            <p class="mt-1 text-[12.5px] text-n-slate-11">
              {{ $t('BASE_IMPORTAR.BAIXAR.CORPO') }}
            </p>
          </div>
          <Button
            :label="$t('BASE_IMPORTAR.BAIXAR.BOTAO')"
            variant="faded"
            color="slate"
            size="sm"
            :is-loading="baixando"
            @click="exportar"
          />
        </section>

        <section
          class="rounded-md border border-solid border-n-weak bg-n-solid-1 px-[18px] py-4 flex flex-col gap-3"
        >
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t('BASE_IMPORTAR.SUBIR.TITULO') }}
            </h2>
            <p class="mt-1 text-[12.5px] text-n-slate-11">
              {{ $t('BASE_IMPORTAR.SUBIR.CORPO') }}
            </p>
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            class="reset-base text-[13px] text-n-slate-11"
            @change="escolher"
          />
          <p v-if="lendo" class="text-[12.5px] text-n-slate-10">
            {{ $t('BASE_IMPORTAR.LENDO') }}
          </p>
        </section>

        <section
          v-if="previa && previa.legivel === false"
          class="rounded-md border border-solid border-n-ruby-6 bg-n-ruby-3 px-[18px] py-4"
        >
          <p class="text-[13px] text-n-ruby-11">
            {{ $t('BASE_IMPORTAR.ILEGIVEL') }}
          </p>
        </section>

        <template v-else-if="previa">
          <section
            class="rounded-md border border-solid border-n-amber-6 bg-n-amber-2 px-[18px] py-4 flex flex-col gap-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="font-mono text-[10px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-teal-3 text-n-teal-11"
              >
                {{ $t('BASE_IMPORTAR.NOVAS', { n: previa.criados || 0 }) }}
              </span>
              <span
                class="font-mono text-[10px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-amber-3 text-n-amber-11"
              >
                {{
                  $t('BASE_IMPORTAR.ATUALIZADAS', {
                    n: previa.atualizados || 0,
                  })
                }}
              </span>
              <span
                class="font-mono text-[10px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-ruby-3 text-n-ruby-11"
              >
                {{
                  $t('BASE_IMPORTAR.SAEM', { n: previa.ausentes?.length || 0 })
                }}
              </span>
              <span
                v-if="previa.ignorados"
                class="font-mono text-[10px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-alpha-2 text-n-slate-11"
              >
                {{ $t('BASE_IMPORTAR.IGNORADAS', { n: previa.ignorados }) }}
              </span>
            </div>
            <p class="text-[12.5px] text-n-amber-11/70">
              {{ $t('BASE_IMPORTAR.RASCUNHO') }}
            </p>
            <!-- Enquanto roda, a espera diz em que passo está. A importação não é rápida
                 (clona a base, cria as novas, desativa as que saem) e sem isso o botão
                 girando por meio minuto parece travado. -->
            <p v-if="aplicando" class="text-[12.5px] text-n-amber-11">
              {{ $t(`BASE_IMPORTAR.FASE.${(fase || 'na_fila').toUpperCase()}`) }}
            </p>
            <div class="flex flex-wrap gap-2">
              <Button
                :label="$t('BASE_IMPORTAR.APLICAR', { n: total })"
                color="amber"
                size="sm"
                :is-loading="aplicando"
                :disabled="aplicando"
                @click="aplicar"
              />
              <Button
                :label="$t('BASE_IMPORTAR.CANCELAR')"
                variant="faded"
                color="slate"
                size="sm"
                :disabled="aplicando"
                @click="previa = null"
              />
            </div>
          </section>

          <!-- O que SAI de cena é a parte que ninguém confere sozinho, então ela vem nomeada. -->
          <section
            v-if="previa.ausentes?.length"
            class="rounded-md border border-solid border-n-weak bg-n-solid-1 px-[18px] py-4 flex flex-col gap-2"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('BASE_IMPORTAR.AUSENTES.TITULO') }}
            </h3>
            <p class="text-[12.5px] text-n-slate-11">
              {{ $t('BASE_IMPORTAR.AUSENTES.CORPO') }}
            </p>
            <div class="flex flex-col gap-1 max-h-[280px] overflow-y-auto">
              <p
                v-for="item in previa.ausentes"
                :key="item.numero_card"
                class="text-[12.5px] text-n-slate-11 break-words"
              >
                <span class="font-mono text-[10.5px] text-n-slate-10">
                  {{ item.numero }}
                </span>
                {{ item.pergunta }}
              </p>
            </div>
          </section>

          <section
            v-if="previa.avisos?.length"
            class="rounded-md border border-solid border-n-weak bg-n-solid-1 px-[18px] py-4 flex flex-col gap-2"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('BASE_IMPORTAR.AVISOS.TITULO') }}
            </h3>
            <div class="flex flex-col gap-1 max-h-[280px] overflow-y-auto">
              <p
                v-for="grupo in avisosAgrupados"
                :key="grupo.mensagem"
                class="text-[12.5px] text-n-slate-11 break-words"
              >
                <span class="font-mono text-[10.5px] text-n-slate-10">
                  {{
                    grupo.linhas.length === 1
                      ? $t('BASE_IMPORTAR.AVISOS.LINHA', { n: grupo.linhas[0] })
                      : $t('BASE_IMPORTAR.AVISOS.LINHAS', {
                          n: grupo.linhas.length,
                          amostra: grupo.linhas.slice(0, 5).join(', '),
                        })
                  }}
                </span>
                {{ grupo.mensagem }}
              </p>
            </div>
          </section>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
